import * as React from 'react';
import * as classNames from 'classnames';
import * as Validator from '../../../utils/validator';
import Captcha from '../../captcha/captcha';
import {GutterSize} from '../../../common/enums';
import pickBy = require('lodash/pickBy');
import keys = require('lodash/keys');
import {Component, FormEvent, KeyboardEvent} from 'react';
import {Input} from '../../input/input';
import {AuthButton as Button} from '../button';
import {IUserRequest} from '../../../common/types';
import {
  COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA,
  KEY_ENTER,
  FieldName,
} from '../service';

interface IAuthSignInFormProps {
  connectionRefusedError?: string;
  isLoading: boolean;
  apiError: string;
  children?: any;
  onSubmit: (data: IUserRequest) => void;
}

interface IAuthSignInFormState {
  fieldNameWithFirstError: string;
  isFormSubmitted: boolean;
  isCaptchaValid: boolean;
  submitCounter: number;
  user: {
    email?: string;
    password?: string;
  };
  errors: {
    email?: string;
    password?: string;
  };
}

export class AuthSignInForm extends Component<IAuthSignInFormProps, IAuthSignInFormState> {
  public state: IAuthSignInFormState = {
    fieldNameWithFirstError: '',
    isFormSubmitted: false,
    isCaptchaValid: false,
    submitCounter: 0,
    user: {
      email: '',
      password: '',
    },
    errors: {
      email: '',
      password: '',
    },
  };

  private emailInput: HTMLElement;

  public componentDidMount(): void {
    this.emailInput.focus();
  }

  public render(): React.ReactElement<any> {
    const {
      connectionRefusedError,
      isLoading,
      children,
    } = this.props;

    const {
      isFormSubmitted,
      errors,
      user,
    } = this.state;

    return (
      <form onSubmit={this.onFormSubmit}>
        <Input
          bottomSpacing={GutterSize.MEDIUM}
          highlightError={this.highlightEmailError()}
          placeholder={'Email'}
          reference={node => this.emailInput = node}
          focused={this.focusOnEmail()}
          value={user.email}
          error={errors.email}
          type={'text'}
          name={'email'}
          onKeyPress={this.onKeyPress}
          onChange={(value) => this.onChange('email', value)}
        />

        <Input
          bottomSpacing={GutterSize.MEDIUM}
          placeholder={'Password'}
          focused={this.focusOnPassword()}
          value={user.password}
          error={errors.password || this.apiError()}
          name={'password'}
          type={'password'}
          onKeyPress={this.onKeyPress}
          onChange={(value) => this.onChange('password', value)}
        />

        <Captcha
          className={classNames({'has-error': this.invalidCaptcha()})}
          isVisible={this.isVisibleCaptcha()}
          verifyCallback={this.verifyCallback}
        />

        {children}

        <Button
          isLoading={isLoading}
          error={isFormSubmitted && connectionRefusedError}
          type={'submit'}
        />
      </form>
    );
  }

  private onFormSubmit = (e: FormEvent<any>): void => {
    e.preventDefault();
    this.send();
  };

  private onKeyPress = (e: KeyboardEvent<any>): void => {
    if (e.keyCode === KEY_ENTER) {
      this.send();
    }
  };

  private onChange = (fieldName: FieldName, value: string): void => {
    const user   = {...this.state.user, ...{[fieldName]: value} };
    const errors = {...this.state.errors, ...{[fieldName]: ''} };

    this.setState({
      user,
      errors,
      isFormSubmitted: false,
    });
  };

  private send = (): void => {
    const {isCaptchaValid, submitCounter} = this.state;
    const {connectionRefusedError} = this.props;
    const isCaptchaVisible = submitCounter >= COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA;
    const isCaptchaCorrect = !isCaptchaVisible || isCaptchaValid;

    this.setState({
      isFormSubmitted: true,
      submitCounter: connectionRefusedError
        ? this.state.submitCounter
        : submitCounter + 1,
    });

    if (this.isFormValid() && isCaptchaCorrect) {
      const {email, password} = this.state.user;
      this.props.onSubmit({email: email.trim(), password});
    }
  };

  private isFormValid = (): boolean => {
    const {user} = this.state;
    const errors = {
      email: Validator.validateEmail(user.email),
      password: Validator.validateRequiredField('Password', user.password),
    };

    const notEmptyErrors = pickBy(errors, (el) => !!el.length);
    const fieldNameWithFirstError = keys(notEmptyErrors)[0] || null;

    // @TODO
    this.setState({errors, fieldNameWithFirstError});

    return !fieldNameWithFirstError;
  };

  private highlightEmailError = (): boolean => {
    const {fieldNameWithFirstError, errors} = this.state;

    return (!!this.apiError() && !fieldNameWithFirstError)
      || (fieldNameWithFirstError === 'email' && !!errors.email);
  };

  private apiError = (): string => {
    return this.state.isFormSubmitted && this.props.apiError;
  };

  private focusOnEmail = (): boolean => {
    const {fieldNameWithFirstError, errors} = this.state;
    return !!this.apiError() || fieldNameWithFirstError === 'email' && !!errors.email;
  };

  private focusOnPassword = (): boolean => {
    const {fieldNameWithFirstError, errors} = this.state;
    return fieldNameWithFirstError === 'password' && !!errors.password;
  };

  private verifyCallback = (): void => {
    this.setState({
      isCaptchaValid: true,
      isFormSubmitted: false,
    });
  };

  private isVisibleCaptcha = (): boolean => {
    return !this.props.connectionRefusedError
      && this.state.submitCounter >= COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA;
  };

  private invalidCaptcha = (): boolean => {
    const {
      isFormSubmitted,
      isCaptchaValid,
      submitCounter,
    } = this.state;

    return submitCounter > COUNT_FAILED_SUBMIT_TO_SHOW_CAPTCHA
      && isFormSubmitted
      && !isCaptchaValid;
  };
}
